
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ui/use-toast";

const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const ResetPasswordDialog = ({ open, onOpenChange, onSuccess }: ResetPasswordDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsLoading(true);
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });

      if (error) throw error;

      // Get the current user to check for pending invitations
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Ensure a profile exists for this user
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();
          
        if (!existingProfile && user.email) {
          console.log('Creating profile for user:', user.id);
          // Create a profile for the user
          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email,
              subscription_tier: 'free',
              role: 'trainee',
              resources_used_this_month: 0,
              total_resources_created: 0,
              billing_status: 'active'
            });
            
          if (profileError) {
            console.error('Error creating profile:', profileError);
          }
        }
        
        // Check for any pending invitations for this email
        if (user.email) {
          const { data: invitations, error: invitationError } = await supabase
            .from('resource_invitations')
            .select('*')
            .eq('email', user.email)
            .eq('status', 'pending');
            
          if (invitationError) {
            console.error('Error checking invitations:', invitationError);
          } else if (invitations && invitations.length > 0) {
            console.log('Found pending invitations for user:', invitations);
          }
        }
      }
      
      // Mark that the user has set a password in their user metadata
      await supabase.auth.updateUser({
        data: { 
          has_set_password: true,
          has_seen_password_prompt: true
        }
      });

      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
      });

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
      navigate('/training');
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create your password</DialogTitle>
          <DialogDescription>
            Set a password to secure your account and access your training resources.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Password"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
